import type { LensFilterValues } from '~/types/leLens';

import { type LensDataReadiness, lensDataReady } from '~/utils/leLensTransport';

import { addDays, lastCompleteDay } from '../ProductPerformance/model';

import { buildVendorData } from './buildVendorData';
import { dealSelections } from './campaignPicker';
import type { Snapshot } from './data';
import { POST_CARDS, livePost } from './live';
import { type Filters, build, defaults } from './model';

export type PostScope = LensFilterValues & {
  offers: Array<string>;
  as_of: string;
  deal_products: Array<string>;
  lens_period: { from: string; to: string };
};
export const postIdentity = (params: URLSearchParams) => params.toString();
export function validDay(value: string): boolean {
  return (
    value.slice(0, 4) !== '0000' &&
    /^\d{4}-\d{2}-\d{2}$/.test(value) &&
    Number.isFinite(Date.parse(value)) &&
    new Date(value).toISOString().slice(0, 10) === value
  );
}
export function productSelection(params: URLSearchParams): Array<string> {
  if (!params.has('fpDeal_product_name')) {
    return ['Limited Time Lux Exclusive'];
  }
  const value: unknown = JSON.parse(params.get('fpDeal_product_name'));
  if (!Array.isArray(value) || value.some((v) => typeof v !== 'string')) {
    throw new Error('Choose valid source deal products.');
  }
  if (value.includes('')) {
    throw new Error('Blank product filtering is unavailable. Choose another source member or All.');
  }
  return value;
}
export function requestPost(params: URLSearchParams, clock: Date): PostScope {
  dealSelections(params);
  const from = params.get('fpfrom') ?? '2025-09-03',
    to = params.get('fpto') ?? '2025-10-16';
  const current = params.get('fpcurrent') ?? '006RF00000LYwIPYA1',
    comparison = params.get('fpcomparison') ?? '006RF00000CixwkYAB';
  const cmpFrom = params.get('fpcmpFrom') ?? '2025-02-18',
    cmpTo = params.get('fpcmpTo') ?? '2025-04-05';
  const starts = [from, ...(comparison ? [cmpFrom] : [])],
    ends = [to, ...(comparison ? [cmpTo] : [])];
  const today = addDays(lastCompleteDay(clock), 1);
  return {
    offers: [...new Set([current, comparison].filter(Boolean))],
    as_of: today,
    deal_products: productSelection(params),
    lens_period: { from: starts.sort()[0], to: ends.sort().slice(-1)[0] },
  };
}
export function scopeProblem(params: URLSearchParams, scope: PostScope): string | null {
  for (const [a, b, needed] of [
    ['fpfrom', 'fpto', true],
    ['fpcmpFrom', 'fpcmpTo', !!(params.get('fpcomparison') ?? '006RF00000CixwkYAB')],
  ] as const) {
    if (!needed) {
      continue;
    }
    const from = params.get(a) ?? (a === 'fpfrom' ? '2025-09-03' : '2025-02-18'),
      to = params.get(b) ?? (b === 'fpto' ? '2025-10-16' : '2025-04-05');
    if (!validDay(from) || !validDay(to) || from > to) {
      return 'Select valid campaign dates in order; figures are withheld.';
    }
  }
  if (!scope.offers.length || params.get('fpcurrent') === '') {
    return 'Choose a current campaign.';
  }
  for (const key of ['fpaxisStart', 'fpaxisDays']) {
    if (params.has(key) && (!/^\d+$/.test(params.get(key)) || !Number.isSafeInteger(Number(params.get(key))))) {
      return 'Choose a valid non-negative chart axis bound.';
    }
  }
  return null;
}
export function readPostFilters(snapshot: Snapshot, params: URLSearchParams): Filters {
  const f = defaults(snapshot);
  const keys: ReadonlyArray<keyof Filters> = [
    'current',
    'comparison',
    'from',
    'to',
    'cmpFrom',
    'cmpTo',
    'refund',
    'currency',
    'room',
    'packageType',
    'booking',
    'section',
  ];
  for (const key of keys) {
    const value = params.get('fp' + key) ?? (['refund', 'currency'].includes(key) ? params.get(key) : null);
    if (value !== null) {
      if (key === 'refund') {
        if (!['Before Refund', 'After Refund'].includes(value)) {
          throw new Error('Choose a supported refund basis.');
        }
        f.refund = value as Filters['refund'];
      } else {
        f[key] = value;
      }
    }
  }
  const current = params.get('fpcurrent') ?? '006RF00000LYwIPYA1';
  const from = params.get('fpfrom') ?? '2025-09-03';
  const to = params.get('fpto') ?? '2025-10-16';
  const nativeCurrency =
    snapshot.sales
      .filter((row) => row.offer_bk === current && row.date >= from && row.date <= to)
      .map((row) => row.cost_currency)
      .filter((currency): currency is string => Boolean(currency))
      .sort()
      .pop() ?? 'AUD';
  return {
    ...f,
    current,
    comparison: params.get('fpcomparison') ?? '006RF00000CixwkYAB',
    from,
    to,
    cmpFrom: params.get('fpcmpFrom') ?? '2025-02-18',
    cmpTo: params.get('fpcmpTo') ?? '2025-04-05',
    currency: params.get('fpcurrency') ?? params.get('currency') ?? nativeCurrency,
  };
}

const SOURCE_LABELS: Record<string, string> = {
  campaigns: 'campaigns',
  sales: 'sales',
  views: 'traffic',
  item_history: 'item history',
  deal_products: 'deal products',
};

/** Describe current-scope sources without treating a partial stream as a report. */
export function postLoadState(input: LensDataReadiness) {
  const current =
    !input.isShowingPreviousData && (!input.responseIdentity || input.responseIdentity === input.requestIdentity);
  const cards = current ? (input.cards ?? []) : [];
  const pending: Array<string> = [],
    failures: Array<string> = [];
  for (const id of POST_CARDS) {
    const matches = cards.filter((card) => card.cardId === id);
    const label = SOURCE_LABELS[id];
    if (matches.length > 1) {
      failures.push(`Duplicate ${label} source results.`);
    } else if (!matches.length) {
      pending.push(label);
    } else if (matches[0].truncated) {
      failures.push(`${label}: source reached its row limit.`);
    } else if (!['ready', 'empty'].includes(matches[0].status)) {
      failures.push(`${label}: ${matches[0].message || 'source could not be loaded.'}`);
    }
  }
  const completed = POST_CARDS.length - pending.length - failures.length;
  if (failures.length) {
    return { completed, failed: true, message: `${failures.join(' ')} Figures and exports are withheld.` };
  }
  if (input.isError) {
    return { completed, failed: true, message: 'Campaign data could not be loaded. Retry to load all sources.' };
  }
  if (input.isFetching) {
    return {
      completed,
      failed: false,
      message: pending.length
        ? `Loading campaign data: ${completed} of ${POST_CARDS.length} sources received. Waiting for ${pending.join(', ')}.`
        : 'All sources received. Waiting for the report response to finish.',
    };
  }
  return {
    completed,
    failed: true,
    message: pending.length
      ? `The report response is missing ${pending.join(', ')}. Retry to load all sources.`
      : 'The report response does not match the current selection. Retry to load this selection.',
  };
}

export function adaptPost(params: URLSearchParams, scope: PostScope, input: LensDataReadiness) {
  const result: {
    ready: boolean;
    failed: boolean;
    completedSources: number;
    problem: string | null;
    identity: string;
    scope: PostScope;
    snapshot?: Snapshot;
    filters?: Filters;
    filtersIdentity?: string;
    model?: ReturnType<typeof build>;
    vendor?: ReturnType<typeof buildVendorData>;
  } = {
    ready: false,
    failed: false,
    completedSources: 0,
    problem: scopeProblem(params, scope),
    identity: postIdentity(params),
    scope,
  };
  if (result.problem) {
    result.failed = true;
    return result;
  }
  if (!lensDataReady(input, POST_CARDS)) {
    const state = postLoadState(input);
    result.problem = state.message;
    result.failed = state.failed;
    result.completedSources = state.completed;
    return result;
  }
  result.completedSources = POST_CARDS.length;
  try {
    const snapshot = livePost(input.cards, scope.lens_period.from, scope.lens_period.to, scope.as_of),
      filters = readPostFilters(snapshot, params),
      model = build(snapshot, filters);
    for (const row of [...snapshot.sales, ...snapshot.views]) {
      if (
        !scope.offers.includes(row.offer_bk) ||
        row.date < scope.lens_period.from ||
        row.date > scope.lens_period.to
      ) {
        throw new Error('Source facts do not match the requested offer/date population.');
      }
    }
    const sourceKeys = new Set(
      snapshot.sales
        .filter((row) => row.orderKey !== null && row.itemKey !== null)
        .map((row) => JSON.stringify([row.orderKey, row.itemKey])),
    );
    if (
      snapshot.history.some(
        (row) =>
          row.orderKey === null || row.itemKey === null || !sourceKeys.has(JSON.stringify([row.orderKey, row.itemKey])),
      )
    ) {
      throw new Error('Source item history has an unselected identity.');
    }
    const historyKeys = new Set(snapshot.history.map((row) => JSON.stringify([row.orderKey, row.itemKey])));
    if ([...sourceKeys].some((key) => !historyKeys.has(key))) {
      throw new Error('Source item history is missing selected identities.');
    }
    result.snapshot = snapshot;
    result.filters = filters;
    result.filtersIdentity = JSON.stringify(filters);
    result.model = model;
    if (model.invalid.length) {
      result.failed = true;
      result.problem = model.invalid.join(' ');
      return result;
    }
    result.vendor = buildVendorData(snapshot, filters);
    const period = params.get('fpperiod') ?? params.get('period') ?? 'Full Period';
    if (
      !result.vendor.variants.some(
        (variant) =>
          variant.currency === filters.currency && variant.refund === filters.refund && variant.period === period,
      )
    ) {
      result.failed = true;
      result.problem = 'Choose a currency and period supported by the current offer.';
      return result;
    }
    result.ready = true;
  } catch (error) {
    result.failed = true;
    result.problem = error instanceof Error ? error.message : 'Invalid live campaign source population.';
  }
  return result;
}

/** A new campaign's date choice uses its source bounds and the injected completed day,
 * independently of the previous request's loaded fact window. */
export function selectLiveCampaign(
  snapshot: Snapshot,
  filters: Filters,
  id: string,
  clock: Date,
  comparison = false,
): Filters {
  const rows = snapshot.campaigns.filter((row) => row.offer_bk === id);
  if (!rows.length) {
    return comparison ? { ...filters, comparison: '' } : { ...filters, current: id };
  }
  const cutoff = lastCompleteDay(clock),
    from = rows.map((row) => row.start_date).sort()[0];
  const ends = rows
    .map((row) => row.end_date)
    .filter((day): day is string => Boolean(day))
    .sort();
  const end = ends.length === rows.length ? ends[ends.length - 1] : cutoff,
    to = end < cutoff ? end : cutoff;
  return comparison
    ? { ...filters, comparison: id, cmpFrom: from, cmpTo: to, room: 'All' }
    : { ...filters, current: id, from, to, room: 'All' };
}
export function liveDefaults(snapshot: Snapshot, clock: Date): Filters {
  const base = defaults({ ...snapshot, meta: { ...snapshot.meta, asOf: lastCompleteDay(clock) } });
  const current = selectLiveCampaign(snapshot, base, base.current, clock);
  return base.comparison ? selectLiveCampaign(snapshot, current, base.comparison, clock, true) : current;
}
